import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateCode(prefix, type) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let rand = '';
  for (let i = 0; i < 4; i++) rand += chars[Math.floor(Math.random() * chars.length)];
  return `${prefix.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)}${type === 'hr' ? 'HR' : ''}${rand}`;
}

function slugify(name) {
  return name.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
}

function avg(arr, key) {
  const vals = arr.map(d => parseFloat(d?.[key])).filter(v => !isNaN(v) && v > 0);
  if (!vals.length) return null;
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}

function statusColor(val, low, high) {
  if (!val) return '#7a9ab8';
  const v = parseFloat(val);
  if (v < low || v > high) return '#f05050';
  if (v > high * 0.85) return '#f0a050';
  return '#00c896';
}

function calcBMI(h, w) {
  if (!h || !w) return null;
  return (parseFloat(w) / Math.pow(parseFloat(h) / 100, 2)).toFixed(1);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, unit, color, sub }) {
  return (
    <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.25rem 1.5rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 500, color: color || '#00AEEF', lineHeight: 1 }}>{value ?? '—'}</div>
      {unit && <div style={{ fontSize: '0.68rem', color: '#7a9ab8', marginTop: '0.3rem', fontWeight: 300 }}>{unit}</div>}
      {sub && <div style={{ fontSize: '0.68rem', color: color || '#7a9ab8', marginTop: '0.2rem', fontWeight: 400 }}>{sub}</div>}
    </div>
  );
}

function MetricBar({ label, value, unit, optLow, optHigh, total, count }) {
  const color = value ? statusColor(value, optLow, optHigh) : '#3d5a72';
  const coverage = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
      <div style={{ width: 140, fontSize: '0.75rem', color: '#7a9ab8', fontWeight: 300, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <div style={{ width: coverage + '%', height: '100%', background: color, borderRadius: 2, transition: 'width 0.8s ease' }} />
      </div>
      <div style={{ width: 60, textAlign: 'right', fontSize: '0.75rem', color, fontWeight: 500, flexShrink: 0 }}>
        {value ? value + ' ' + unit : '—'}
      </div>
      <div style={{ width: 40, textAlign: 'right', fontSize: '0.65rem', color: '#3d5a72', flexShrink: 0 }}>
        {count}/{total}
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  const cfg = { High: { bg: 'rgba(240,80,80,0.1)', color: '#f05050', border: 'rgba(240,80,80,0.3)' }, Moderate: { bg: 'rgba(240,160,80,0.1)', color: '#f0a050', border: 'rgba(240,160,80,0.3)' }, Low: { bg: 'rgba(0,200,150,0.1)', color: '#00c896', border: 'rgba(0,200,150,0.3)' } };
  const c = cfg[level] || cfg.Low;
  return <span style={{ padding: '0.2rem 0.6rem', borderRadius: 100, background: c.bg, color: c.color, border: `0.5px solid ${c.border}`, fontSize: '0.68rem', fontWeight: 500 }}>{level}</span>;
}

// ── Data loader ───────────────────────────────────────────────────────────────

async function loadCompanyHealthData(employeeCode, members) {
  const employeeUids = members.filter(m => m.role === 'employee' || !m.role).map(m => m.uid);
  const blood = [], body = [], mental = [], fitness = [];

  const training = [];
  const nutrition = [];
  await Promise.all(employeeUids.map(async uid => {
    try {
      const [b, eb, bo, m, f, tp] = await Promise.all([
        getDoc(doc(db, 'users', uid, 'latest', 'blood')),
        getDoc(doc(db, 'users', uid, 'latest', 'extendedBlood')),
        getDoc(doc(db, 'users', uid, 'latest', 'body')),
        getDoc(doc(db, 'users', uid, 'latest', 'mental')),
        getDoc(doc(db, 'users', uid, 'latest', 'fitness')),
        getDoc(doc(db, 'users', uid, 'latest', 'trainingProfile'))
      ]);
      const combined = { ...(b.exists() ? b.data() : {}), ...(eb.exists() ? eb.data() : {}) };
      if (Object.keys(combined).length > 0) blood.push(combined);
      if (bo.exists()) body.push(bo.data());
      if (m.exists()) mental.push(m.data());
      if (f.exists()) fitness.push(f.data());
      if (tp.exists()) training.push(tp.data());

      // Load nutrition profile
      try {
        const np = await getDoc(doc(db, 'users', uid, 'latest', 'nutritionProfile'));
        const ng = await getDoc(doc(db, 'users', uid, 'latest', 'nutritionGamification'));
        if (np.exists()) {
          const npData = np.data();
          if (ng.exists()) npData._nutritionGam = ng.data();
          nutrition.push(npData);
        }
      } catch(e) {}

      // Load session count from training history
      try {
        const sessSnap = await getDocs(collection(db, 'users', uid, 'history', 'training', 'entries'));
        if (tp.exists()) {
          training[training.length - 1]._sessionCount = sessSnap.size;
        }
      } catch (e) {}
    } catch (e) {}
  }));

  return { blood, body, mental, fitness, training, nutrition, totalEmployees: employeeUids.length };
}

// ── Company Detail View ───────────────────────────────────────────────────────

function CompanyDetail({ company, members, onClose }) {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const data = await loadCompanyHealthData(company.employeeCode, members);
      setHealthData(data);
      setLoading(false);
    }
    load();
  }, [company.employeeCode]);

  if (loading) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#7a9ab8', fontSize: '0.85rem' }}>Loading health data…</div>
  );

  const { blood, body, mental, fitness, totalEmployees } = healthData;
  const avgGlucose = avg(blood, 'fastingGlucose');
  const avgSystolic = avg(blood, 'systolicBP');
  const avgCholesterol = avg(blood, 'totalCholesterol');
  const avgVitD = avg(blood, 'vitaminD');
  const avgCRP = avg(blood, 'crp');
  const avgStress = avg(mental, 'stressScore');
  const avgBurnout = avg(mental, 'burnoutScore');
  const avgSleep = avg(mental, 'sleepHours');
  const avgSteps = avg(fitness, 'stepsPerDay');
  const bmis = body.map(b => calcBMI(b.height, b.weight)).filter(Boolean);
  const avgBMI = bmis.length ? (bmis.reduce((a, b) => a + parseFloat(b), 0) / bmis.length).toFixed(1) : null;

  const highRisk = blood.filter(b => parseFloat(b.fastingGlucose) >= 7 || parseFloat(b.systolicBP) >= 140 || parseFloat(b.totalCholesterol) >= 6).length;
  const moderate = blood.filter(b => (parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7) || (parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140)).length;

  return (
    <div style={{ background: '#0a1828', border: '0.5px solid rgba(0,174,239,0.2)', borderRadius: 8, padding: '1.5rem', marginTop: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontSize: '1rem', fontWeight: 500, color: '#e8f0f8', marginBottom: '0.2rem' }}>{company.name} — Health Overview</div>
          <div style={{ fontSize: '0.72rem', color: '#7a9ab8' }}>{totalEmployees} employees · {blood.length} with blood data · {mental.length} with mental health data</div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#7a9ab8', fontSize: '0.75rem', padding: '0.3rem 0.75rem', cursor: 'pointer' }}>Close ✕</button>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <StatCard label="Avg Glucose" value={avgGlucose} unit="mmol/L" color={avgGlucose ? statusColor(avgGlucose, 3.5, 5.5) : null} />
        <StatCard label="Avg Systolic BP" value={avgSystolic} unit="mmHg" color={avgSystolic ? statusColor(avgSystolic, 90, 130) : null} />
        <StatCard label="Avg Cholesterol" value={avgCholesterol} unit="mmol/L" color={avgCholesterol ? statusColor(avgCholesterol, 2, 5) : null} />
        <StatCard label="Avg Stress" value={avgStress} unit="/100" color={avgStress ? statusColor(100 - parseFloat(avgStress), 35, 100) : null} />
        <StatCard label="Avg Sleep" value={avgSleep} unit="hrs/night" color={avgSleep ? (parseFloat(avgSleep) >= 7 ? '#00c896' : '#f0a050') : null} />
        <StatCard label="Avg BMI" value={avgBMI} unit="kg/m²" color={avgBMI ? (parseFloat(avgBMI) < 25 ? '#00c896' : parseFloat(avgBMI) < 30 ? '#f0a050' : '#f05050') : null} />
      </div>

      {/* Risk distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.1rem' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>Risk Distribution</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[['High', highRisk, '#f05050'], ['Moderate', moderate, '#f0a050'], ['Low', Math.max(0, totalEmployees - highRisk - moderate), '#00c896']].map(([label, count, color]) => (
              <div key={label} style={{ flex: 1, textAlign: 'center', background: color + '12', border: `0.5px solid ${color}44`, borderRadius: 6, padding: '0.75rem 0.5rem' }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', color, fontWeight: 500, lineHeight: 1 }}>{count}</div>
                <div style={{ fontSize: '0.65rem', color, marginTop: '0.3rem', fontWeight: 500 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.1rem' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Data Coverage</div>
          {[['Blood results', blood.length], ['Mental health', mental.length], ['Body metrics', body.length], ['Fitness data', fitness.length]].map(([label, count]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.72rem', color: '#7a9ab8', width: 100, flexShrink: 0, fontWeight: 300 }}>{label}</div>
              <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <div style={{ width: (totalEmployees > 0 ? count / totalEmployees * 100 : 0) + '%', height: '100%', background: '#00AEEF', borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: '0.65rem', color: '#7a9ab8', width: 40, textAlign: 'right', flexShrink: 0 }}>{count}/{totalEmployees}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed markers */}
      <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.1rem' }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Biomarker Averages</div>
        <MetricBar label="Fasting Glucose" value={avgGlucose} unit="mmol/L" optLow={3.5} optHigh={5.5} total={totalEmployees} count={blood.filter(b => b.fastingGlucose).length} />
        <MetricBar label="Systolic BP" value={avgSystolic} unit="mmHg" optLow={90} optHigh={130} total={totalEmployees} count={blood.filter(b => b.systolicBP).length} />
        <MetricBar label="Total Cholesterol" value={avgCholesterol} unit="mmol/L" optLow={2} optHigh={5} total={totalEmployees} count={blood.filter(b => b.totalCholesterol).length} />
        <MetricBar label="Vitamin D" value={avgVitD} unit="nmol/L" optLow={50} optHigh={200} total={totalEmployees} count={blood.filter(b => b.vitaminD).length} />
        <MetricBar label="CRP (inflammation)" value={avgCRP} unit="mg/L" optLow={0} optHigh={5} total={totalEmployees} count={blood.filter(b => b.crp).length} />
        <MetricBar label="Stress Score" value={avgStress} unit="/100" optLow={0} optHigh={40} total={totalEmployees} count={mental.filter(m => m.stressScore).length} />
        <MetricBar label="Burnout Score" value={avgBurnout} unit="/100" optLow={0} optHigh={40} total={totalEmployees} count={mental.filter(m => m.burnoutScore).length} />
        <MetricBar label="Avg Steps/Day" value={avgSteps} unit="steps" optLow={8000} optHigh={20000} total={totalEmployees} count={fitness.filter(f => f.stepsPerDay).length} />
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function LevareAdmin({ user, profile, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'overview');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allHealthData, setAllHealthData] = useState({});
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [allMemberData, setAllMemberData] = useState({});

  // Company creation
  const [creating, setCreating] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'companies'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCompanies(list);

      const memberData = {};
      for (const company of list) {
        const path = company.employeeCode || company.id;
        try {
          const membersSnap = await getDocs(collection(db, 'companies', path, 'members'));
          memberData[company.id] = membersSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
        } catch (e) { memberData[company.id] = []; }
      }
      setAllMemberData(memberData);

      // Load health data for all companies
      setLoadingHealth(true);
      const healthData = {};
      for (const company of list) {
        const members = memberData[company.id] || [];
        healthData[company.id] = await loadCompanyHealthData(company.employeeCode || company.id, members);
      }
      setAllHealthData(healthData);
      setLoadingHealth(false);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleCreateCompany(e) {
    e.preventDefault();
    if (!newCompanyName.trim()) return;
    setCreating(true); setCreateError(''); setCreateSuccess('');

    const slug = slugify(newCompanyName);
    const employeeCode = generateCode(slug, 'employee');
    const hrCode = generateCode(slug, 'hr');
    const companyName = newCompanyName.trim();

    try {
      const existing = companies.find(c => c.name?.toLowerCase() === companyName.toLowerCase());
      if (existing) { setCreateError('A company with this name already exists.'); setCreating(false); return; }

      await setDoc(doc(db, 'companies', employeeCode), { name: companyName, employeeCode, hrCode, createdAt: serverTimestamp(), createdBy: user.uid, active: true });
      await setDoc(doc(db, 'codeLookup', employeeCode), { role: 'employee', companyCode: employeeCode, companyName });
      await setDoc(doc(db, 'codeLookup', hrCode), { role: 'hr', companyCode: employeeCode, companyName });

      setCreateSuccess(`Company created! Employee code: ${employeeCode} | HR code: ${hrCode}`);
      setNewCompanyName('');
      await loadAll();
    } catch (e) { setCreateError('Error creating company. Please try again.'); }
    setCreating(false);
  }

  async function handleDeleteCompany(companyId) {
    if (!window.confirm('Delete this company? This cannot be undone.')) return;
    try {
      const company = companies.find(c => c.id === companyId);
      await deleteDoc(doc(db, 'companies', company?.employeeCode || companyId));
      await loadAll();
    } catch (e) { alert('Error deleting company.'); }
  }

  // Platform-wide aggregated stats
  const allBlood = Object.values(allHealthData).flatMap(d => d.blood || []);
  const allMental = Object.values(allHealthData).flatMap(d => d.mental || []);
  const allBody = Object.values(allHealthData).flatMap(d => d.body || []);
  const allFitness = Object.values(allHealthData).flatMap(d => d.fitness || []);
  const totalEmployees = Object.values(allHealthData).reduce((s, d) => s + (d.totalEmployees || 0), 0);
  const totalHR = Object.values(allMemberData).flat().filter(m => m.role === 'hr').length;

  const platformAvgGlucose = avg(allBlood, 'fastingGlucose');
  const platformAvgBP = avg(allBlood, 'systolicBP');
  const platformAvgCholesterol = avg(allBlood, 'totalCholesterol');
  const platformAvgStress = avg(allMental, 'stressScore');
  const platformAvgSleep = avg(allMental, 'sleepHours');
  const allBMIs = allBody.map(b => calcBMI(b.height, b.weight)).filter(Boolean);
  const platformAvgBMI = allBMIs.length ? (allBMIs.reduce((a, b) => a + parseFloat(b), 0) / allBMIs.length).toFixed(1) : null;

  const highRiskTotal = allBlood.filter(b => parseFloat(b.fastingGlucose) >= 7 || parseFloat(b.systolicBP) >= 140 || parseFloat(b.totalCholesterol) >= 6).length;
  const moderateTotal = allBlood.filter(b => (parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7) || (parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140)).length;

  const TABS = [
    { id: 'overview', label: 'Platform Overview' },
    { id: 'companies', label: 'Company Management' },
    { id: 'clinical', label: 'Clinical Flags' },
    { id: 'fitness', label: 'Fitness Trends' },
    { id: 'nutrition', label: 'Nutrition Trends' },
  ];

  if (loading) return (
    <div style={{ padding: '2rem 0', color: '#7a9ab8', fontSize: '0.9rem' }}>Loading platform data…</div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00AEEF', marginBottom: '0.5rem' }}>Levare Admin</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 500, color: '#e8f0f8', marginBottom: '0.4rem' }}>Platform Management</h1>
        <p style={{ fontSize: '0.88rem', color: '#7a9ab8', fontWeight: 300 }}>
          {companies.length} companies · {totalEmployees} employees · {totalHR} HR managers
          {loadingHealth && ' · Loading health data…'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.06)', marginBottom: '2rem' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '0.7rem 1.5rem', fontSize: '0.85rem', color: tab === t.id ? '#00AEEF' : '#7a9ab8', cursor: 'pointer', border: 'none', background: 'none', borderBottom: tab === t.id ? '2px solid #00AEEF' : '2px solid transparent', marginBottom: '-0.5px', fontFamily: "'DM Sans', sans-serif", transition: 'color 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PLATFORM OVERVIEW ── */}
      {tab === 'overview' && (
        <div>
          {/* Platform stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.85rem', marginBottom: '1.5rem' }}>
            <StatCard label="Companies" value={companies.length} color="#00AEEF" />
            <StatCard label="Employees" value={totalEmployees} color="#00c896" />
            <StatCard label="HR Managers" value={totalHR} color="#f0a050" />
            <StatCard label="Avg Glucose" value={platformAvgGlucose} unit="mmol/L" color={platformAvgGlucose ? statusColor(platformAvgGlucose, 3.5, 5.5) : null} />
            <StatCard label="Avg Systolic BP" value={platformAvgBP} unit="mmHg" color={platformAvgBP ? statusColor(platformAvgBP, 90, 130) : null} />
            <StatCard label="Avg Stress" value={platformAvgStress} unit="/100" color={platformAvgStress ? (parseFloat(platformAvgStress) < 40 ? '#00c896' : parseFloat(platformAvgStress) < 65 ? '#f0a050' : '#f05050') : null} />
            <StatCard label="Avg Sleep" value={platformAvgSleep} unit="hrs" color={platformAvgSleep ? (parseFloat(platformAvgSleep) >= 7 ? '#00c896' : '#f0a050') : null} />
            <StatCard label="Avg BMI" value={platformAvgBMI} unit="kg/m²" color={platformAvgBMI ? (parseFloat(platformAvgBMI) < 25 ? '#00c896' : parseFloat(platformAvgBMI) < 30 ? '#f0a050' : '#f05050') : null} />
          </div>

          {/* Risk distribution */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.25rem' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>Platform Risk Distribution</div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {[['High', highRiskTotal, '#f05050'], ['Moderate', moderateTotal, '#f0a050'], ['Low', Math.max(0, allBlood.length - highRiskTotal - moderateTotal), '#00c896']].map(([label, count, color]) => (
                  <div key={label} style={{ flex: 1, textAlign: 'center', background: color + '12', border: `0.5px solid ${color}44`, borderRadius: 6, padding: '1rem 0.5rem' }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color, fontWeight: 500, lineHeight: 1 }}>{count}</div>
                    <div style={{ fontSize: '0.65rem', color, marginTop: '0.4rem', fontWeight: 500 }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.25rem' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Platform Biomarker Averages</div>
              <MetricBar label="Fasting Glucose" value={platformAvgGlucose} unit="mmol/L" optLow={3.5} optHigh={5.5} total={totalEmployees} count={allBlood.filter(b => b.fastingGlucose).length} />
              <MetricBar label="Systolic BP" value={platformAvgBP} unit="mmHg" optLow={90} optHigh={130} total={totalEmployees} count={allBlood.filter(b => b.systolicBP).length} />
              <MetricBar label="Total Cholesterol" value={platformAvgCholesterol} unit="mmol/L" optLow={2} optHigh={5} total={totalEmployees} count={allBlood.filter(b => b.totalCholesterol).length} />
              <MetricBar label="Stress Score" value={platformAvgStress} unit="/100" optLow={0} optHigh={40} total={totalEmployees} count={allMental.filter(m => m.stressScore).length} />
              <MetricBar label="Sleep" value={platformAvgSleep} unit="hrs" optLow={7} optHigh={9} total={totalEmployees} count={allMental.filter(m => m.sleepHours).length} />
            </div>
          </div>

          {/* Per-company breakdown */}
          <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Company Breakdown — click to expand
            </div>
            {companies.map(company => {
              const hd = allHealthData[company.id] || {};
              const members = allMemberData[company.id] || [];
              const employees = members.filter(m => m.role === 'employee' || !m.role).length;
              const glucose = avg(hd.blood || [], 'fastingGlucose');
              const stress = avg(hd.mental || [], 'stressScore');
              const bp = avg(hd.blood || [], 'systolicBP');
              const isExpanded = expandedCompany === company.id;

              return (
                <div key={company.id}>
                  <div onClick={() => setExpandedCompany(isExpanded ? null : company.id)}
                    style={{ display: 'flex', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.04)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,174,239,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 500, color: '#00AEEF', flexShrink: 0, marginRight: '1rem' }}>
                      {company.name?.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#e8f0f8' }}>{company.name}</div>
                      <div style={{ fontSize: '0.72rem', color: '#7a9ab8', marginTop: '0.1rem' }}>{employees} employee{employees !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
                      {[['Glucose', glucose, 'mmol/L', 3.5, 5.5], ['BP', bp, 'mmHg', 90, 130], ['Stress', stress, '/100', 0, 40]].map(([label, val, unit, lo, hi]) => (
                        <div key={label} style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.65rem', color: '#7a9ab8', marginBottom: '0.1rem' }}>{label}</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 500, color: val ? statusColor(val, lo, hi) : '#3d5a72' }}>{val || '—'}</div>
                        </div>
                      ))}
                      <span style={{ color: '#7a9ab8', fontSize: '0.7rem', marginLeft: '0.5rem' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '0 1rem 1rem' }}>
                      <CompanyDetail company={company} members={members} onClose={() => setExpandedCompany(null)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── COMPANY MANAGEMENT ── */}
      {tab === 'companies' && (
        <div>

          <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.5rem', marginBottom: '2rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7a9ab8', marginBottom: '1rem' }}>Create New Company</div>
            <form onSubmit={handleCreateCompany} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <input value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)} placeholder="Company name e.g. Acme Corp"
                  style={{ background: '#080f18', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 2, padding: '0.75rem 1rem', color: '#e8f0f8', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', outline: 'none', width: '100%' }} required />
              </div>
              <button type="submit" disabled={creating} style={{ background: '#00AEEF', color: '#080f18', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: '0.9rem', padding: '0.75rem 1.5rem', border: 'none', borderRadius: 2, cursor: 'pointer', flexShrink: 0 }}>
                {creating ? 'Creating…' : '+ Create Company'}
              </button>
            </form>
            {createError && <div style={{ background: 'rgba(240,80,80,0.1)', border: '0.5px solid #f05050', color: '#f05050', padding: '0.75rem 1rem', borderRadius: 2, fontSize: '0.82rem', marginTop: '1rem' }}>{createError}</div>}
            {createSuccess && <div style={{ background: 'rgba(0,200,150,0.1)', border: '0.5px solid #00c896', color: '#00c896', padding: '0.75rem 1rem', borderRadius: 2, fontSize: '0.85rem', marginTop: '1rem', lineHeight: 1.6 }}>✓ {createSuccess}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {companies.map(company => {
              const members = allMemberData[company.id] || [];
              const employees = members.filter(m => m.role === 'employee' || !m.role).length;
              const hrManagers = members.filter(m => m.role === 'hr').length;
              const isExpanded = expandedCompany === company.id;

              return (
                <div key={company.id} style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', cursor: 'pointer' }}
                    onClick={() => setExpandedCompany(isExpanded ? null : company.id)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,174,239,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 500, color: '#00AEEF', flexShrink: 0 }}>
                        {company.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#e8f0f8' }}>{company.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#7a9ab8', marginTop: '0.15rem' }}>{employees} employee{employees !== 1 ? 's' : ''} · {hrManagers} HR manager{hrManagers !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ background: 'rgba(0,174,239,0.1)', border: '0.5px solid rgba(0,174,239,0.3)', color: '#00AEEF', fontSize: '0.68rem', padding: '0.2rem 0.6rem', borderRadius: 100, fontWeight: 500 }}>Active</span>
                      <span style={{ color: '#7a9ab8', fontSize: '0.7rem' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)', padding: '1.25rem 1.5rem', background: '#0a1828' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                        <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(0,200,150,0.2)', borderRadius: 6, padding: '1rem' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Employee Code</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700, color: '#00c896', letterSpacing: '0.1em' }}>{company.employeeCode}</div>
                          <div style={{ fontSize: '0.72rem', color: '#7a9ab8', marginTop: '0.3rem' }}>Share with employees</div>
                        </div>
                        <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(240,160,80,0.2)', borderRadius: 6, padding: '1rem' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>HR Manager Code</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700, color: '#f0a050', letterSpacing: '0.1em' }}>{company.hrCode}</div>
                          <div style={{ fontSize: '0.72rem', color: '#7a9ab8', marginTop: '0.3rem' }}>Share with HR manager only</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleDeleteCompany(company.id)} style={{ background: 'rgba(240,80,80,0.1)', border: '0.5px solid rgba(240,80,80,0.3)', color: '#f05050', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', padding: '0.4rem 1rem', borderRadius: 2, cursor: 'pointer' }}>
                          Delete Company
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── FITNESS TRENDS ── */}
      {tab === 'fitness' && (
        <div>
          {/* Platform fitness overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.85rem', marginBottom: '1.5rem' }}>
            {(() => {
              const allTraining = Object.values(allHealthData).flatMap(d => d.training || []);
              const allFitness = Object.values(allHealthData).flatMap(d => d.fitness || []);
              const withTraining = allTraining.filter(t => t.setupComplete).length;
              const totalSessions = allTraining.reduce((sum, t) => sum + (t._sessionCount || 0), 0);
              const avgSteps = avg(allFitness, 'stepsPerDay');
              const avgRHR = avg(allFitness, 'restingHR');
              const goals = allTraining.reduce((acc, t) => { if (t.goal) acc[t.goal] = (acc[t.goal] || 0) + 1; return acc; }, {});
              const topGoal = Object.entries(goals).sort((a, b) => b[1] - a[1])[0];
              return <>
                <StatCard label="Using Rico PT" value={withTraining} color="#00c896" sub={`of ${totalEmployees} employees`} />
                <StatCard label="Total Sessions Logged" value={totalSessions} color="#00AEEF" sub="across all companies" />
                <StatCard label="Avg Daily Steps" value={avgSteps} unit="steps/day" color={avgSteps ? (parseFloat(avgSteps) >= 8000 ? '#00c896' : '#f0a050') : null} />
                <StatCard label="Avg Resting HR" value={avgRHR} unit="bpm" color={avgRHR ? (parseFloat(avgRHR) < 70 ? '#00c896' : '#f0a050') : null} />
                <StatCard label="Top Training Goal" value={topGoal ? topGoal[0].split(' ')[0] : '—'} color="#a78bfa" sub={topGoal ? `${topGoal[1]} employee${topGoal[1] !== 1 ? 's' : ''}` : null} />
              </>;
            })()}
          </div>

          {/* Per company fitness breakdown */}
          <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Fitness Data by Company
            </div>
            {companies.map(company => {
              const hd = allHealthData[company.id] || {};
              const training = hd.training || [];
              const fitness = hd.fitness || [];
              const withPT = training.filter(t => t.setupComplete).length;
              const steps = avg(fitness, 'stepsPerDay');
              const rhr = avg(fitness, 'restingHR');
              const active = avg(fitness, 'activeMinutes');
              const workouts = avg(fitness, 'workoutsPerWeek');
              const goals = training.reduce((acc, t) => { if (t.goal) acc[t.goal] = (acc[t.goal] || 0) + 1; return acc; }, {});

              return (
                <div key={company.id} style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.04)', display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ width: 160, flexShrink: 0 }}>
                    <div style={{ fontSize: '.85rem', fontWeight: 500, color: '#e8f0f8' }}>{company.name}</div>
                    <div style={{ fontSize: '.7rem', color: '#7a9ab8', marginTop: '.1rem' }}>{withPT}/{(allMemberData[company.id] || []).length} using Rico PT</div>
                    <div style={{ fontSize: '.7rem', color: '#00c896', marginTop: '.1rem' }}>{training.reduce((s, t) => s + (t._sessionCount || 0), 0)} sessions logged</div>
                  </div>
                  {[
                    ['Steps/day', steps, '8000', '20000'],
                    ['Resting HR', rhr, '50', '70'],
                    ['Active min', active, '30', '999'],
                    ['Workouts/wk', workouts, '3', '7'],
                  ].map(([label, val, lo, hi]) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: '#7a9ab8', marginBottom: '.15rem' }}>{label}</div>
                      <div style={{ fontSize: '.88rem', fontWeight: 500, color: val ? statusColor(val, parseFloat(lo), parseFloat(hi)) : '#3d5a72' }}>{val || '—'}</div>
                    </div>
                  ))}
                  {Object.keys(goals).length > 0 && (
                    <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                      {Object.entries(goals).map(([goal, count]) => (
                        <span key={goal} style={{ padding: '.2rem .5rem', borderRadius: 4, background: 'rgba(0,174,239,0.1)', border: '0.5px solid rgba(0,174,239,0.2)', fontSize: '.65rem', color: '#00AEEF' }}>
                          {goal.split(' ')[0]} ×{count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Training goals distribution */}
          {(() => {
            const allTraining = Object.values(allHealthData).flatMap(d => d.training || []);
            const goals = allTraining.reduce((acc, t) => { if (t.goal) acc[t.goal] = (acc[t.goal] || 0) + 1; return acc; }, {});
            if (Object.keys(goals).length === 0) return null;
            return (
              <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>Platform Training Goals Distribution</div>
                <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
                  {Object.entries(goals).sort((a, b) => b[1] - a[1]).map(([goal, count]) => (
                    <div key={goal} style={{ padding: '.75rem 1.25rem', background: 'rgba(0,174,239,0.08)', border: '0.5px solid rgba(0,174,239,0.2)', borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', color: '#00AEEF', fontWeight: 500 }}>{count}</div>
                      <div style={{ fontSize: '.72rem', color: '#7a9ab8', marginTop: '.3rem', fontWeight: 300 }}>{goal}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── NUTRITION TRENDS ── */}
      {tab === 'nutrition' && (
        <div>
          {/* Platform nutrition stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '.85rem', marginBottom: '1.5rem' }}>
            {(() => {
              const allNutrition = Object.values(allHealthData).flatMap(d => d.nutrition || []);
              const withPlan = allNutrition.filter(n => n.setupComplete).length;
              const avgCalTarget = avg(allNutrition, 'targetCalories');
              const avgProteinTarget = avg(allNutrition, 'proteinTarget');
              const totalXP = allNutrition.reduce((s, n) => s + (n._nutritionGam?.xp || 0), 0);
              const avgStreak = allNutrition.length > 0 ? Math.round(allNutrition.reduce((s, n) => s + (n._nutritionGam?.streak || 0), 0) / allNutrition.length) : 0;
              const goals = allNutrition.reduce((acc, n) => { if (n.primaryGoal) acc[n.primaryGoal] = (acc[n.primaryGoal] || 0) + 1; return acc; }, {});
              const topGoal = Object.entries(goals).sort((a, b) => b[1] - a[1])[0];
              return <>
                <StatCard label="Using Rico Nutrition" value={withPlan} color="#f0a050" sub={`of ${totalEmployees} employees`} />
                <StatCard label="Avg Calorie Target" value={avgCalTarget} unit="kcal/day" color="#f0a050" />
                <StatCard label="Avg Protein Target" value={avgProteinTarget} unit="g/day" color="#00c896" />
                <StatCard label="Avg Log Streak" value={avgStreak} unit="days" color="#00AEEF" />
                <StatCard label="Total Nutrition XP" value={totalXP} color="#a78bfa" sub="across platform" />
                <StatCard label="Top Goal" value={topGoal ? topGoal[0].replace('_',' ') : '—'} color="#f0a050" sub={topGoal ? topGoal[1] + ' employees' : null} />
              </>;
            })()}
          </div>

          {/* Per company nutrition breakdown */}
          <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Nutrition Data by Company
            </div>
            {companies.map(company => {
              const hd = allHealthData[company.id] || {};
              const nutrition = hd.nutrition || [];
              const members = allMemberData[company.id] || [];
              const withPlan = nutrition.filter(n => n.setupComplete).length;
              const avgCal = avg(nutrition, 'targetCalories');
              const avgProt = avg(nutrition, 'proteinTarget');
              const avgStreak = nutrition.length > 0 ? Math.round(nutrition.reduce((s,n) => s + (n._nutritionGam?.streak||0), 0) / nutrition.length) : null;
              const goals = nutrition.reduce((acc, n) => { if (n.primaryGoal) acc[n.primaryGoal] = (acc[n.primaryGoal]||0)+1; return acc; }, {});

              return (
                <div key={company.id} style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.04)', display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ width: 160, flexShrink: 0 }}>
                    <div style={{ fontSize: '.85rem', fontWeight: 500, color: '#e8f0f8' }}>{company.name}</div>
                    <div style={{ fontSize: '.7rem', color: '#7a9ab8', marginTop: '.1rem' }}>{withPlan}/{members.length} using Rico Nutrition</div>
                  </div>
                  {[
                    ['Avg target', avgCal ? avgCal+' kcal' : '—', '#f0a050'],
                    ['Avg protein', avgProt ? avgProt+'g' : '—', '#00c896'],
                    ['Avg streak', avgStreak !== null ? avgStreak+'d 🔥' : '—', '#f0a050'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '.65rem', color: '#7a9ab8', marginBottom: '.15rem' }}>{label}</div>
                      <div style={{ fontSize: '.88rem', fontWeight: 500, color }}>{val}</div>
                    </div>
                  ))}
                  {Object.keys(goals).length > 0 && (
                    <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                      {Object.entries(goals).map(([goal, count]) => (
                        <span key={goal} style={{ padding: '.2rem .5rem', borderRadius: 4, background: 'rgba(240,160,80,0.1)', border: '0.5px solid rgba(240,160,80,0.2)', fontSize: '.65rem', color: '#f0a050' }}>
                          {goal.replace('_',' ')} ×{count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CLINICAL FLAGS ── */}
      {tab === 'clinical' && (
        <div>
          <div style={{ background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', fontWeight: 500, color: '#7a9ab8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              High-Risk Markers by Company
            </div>
            {companies.map(company => {
              const hd = allHealthData[company.id] || {};
              const blood = hd.blood || [];
              const mental = hd.mental || [];
              const flags = [];
              const glucose = avg(blood, 'fastingGlucose');
              const bp = avg(blood, 'systolicBP');
              const chol = avg(blood, 'totalCholesterol');
              const stress = avg(mental, 'stressScore');
              const burnout = avg(mental, 'burnoutScore');
              const vitD = avg(blood, 'vitaminD');
              const crp = avg(blood, 'crp');

              if (glucose && parseFloat(glucose) >= 5.6) flags.push({ marker: 'Fasting Glucose', value: glucose + ' mmol/L', risk: parseFloat(glucose) >= 7 ? 'High' : 'Moderate' });
              if (bp && parseFloat(bp) >= 120) flags.push({ marker: 'Blood Pressure', value: bp + ' mmHg systolic', risk: parseFloat(bp) >= 140 ? 'High' : 'Moderate' });
              if (chol && parseFloat(chol) >= 5) flags.push({ marker: 'Cholesterol', value: chol + ' mmol/L', risk: parseFloat(chol) >= 6 ? 'High' : 'Moderate' });
              if (stress && parseFloat(stress) >= 60) flags.push({ marker: 'Stress Score', value: stress + '/100', risk: parseFloat(stress) >= 75 ? 'High' : 'Moderate' });
              if (burnout && parseFloat(burnout) >= 55) flags.push({ marker: 'Burnout Score', value: burnout + '/100', risk: 'Moderate' });
              if (vitD && parseFloat(vitD) < 30) flags.push({ marker: 'Vitamin D', value: vitD + ' nmol/L', risk: 'High' });
              if (crp && parseFloat(crp) > 5) flags.push({ marker: 'Inflammation (CRP)', value: crp + ' mg/L', risk: 'Moderate' });

              if (flags.length === 0) return (
                <div key={company.id} style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.04)', gap: '1rem' }}>
                  <div style={{ width: 140, fontSize: '0.85rem', fontWeight: 500, color: '#e8f0f8' }}>{company.name}</div>
                  <span style={{ fontSize: '0.72rem', color: '#00c896' }}>✓ No clinical flags</span>
                </div>
              );

              return (
                <div key={company.id} style={{ borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ padding: '0.85rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ width: 140, fontSize: '0.85rem', fontWeight: 500, color: '#e8f0f8', flexShrink: 0 }}>{company.name}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {flags.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.6rem', background: f.risk === 'High' ? 'rgba(240,80,80,0.08)' : 'rgba(240,160,80,0.08)', border: `0.5px solid ${f.risk === 'High' ? 'rgba(240,80,80,0.3)' : 'rgba(240,160,80,0.3)'}`, borderRadius: 4 }}>
                          <RiskBadge level={f.risk} />
                          <span style={{ fontSize: '0.72rem', color: '#7a9ab8' }}>{f.marker}:</span>
                          <span style={{ fontSize: '0.72rem', color: '#e8f0f8', fontWeight: 500 }}>{f.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ background: 'rgba(0,174,239,0.04)', border: '0.5px solid rgba(0,174,239,0.15)', borderRadius: 8, padding: '1rem 1.25rem', fontSize: '0.75rem', color: '#7a9ab8', fontWeight: 300, lineHeight: 1.6 }}>
            Clinical flags show population-level averages only. No individual data is identifiable. Flags are triggered when company averages exceed NHS/WHO optimal thresholds.
          </div>
        </div>
      )}
    </div>
  );
}
